import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { SignInPage } from '../pages/SignInPage';
import { AccountPage } from '../pages/AccountPage';
import { ClientsPage } from '../pages/ClientsPage';
import { DeviceCatalogPage } from '../pages/DeviceCatalogPage';
import { ManufacturedDevicesPage } from '../pages/ManufacturedDevicesPage';
import { StaffPage } from '../pages/StaffPage';
import { can, isAdmin, useStaffMe } from '../lib/permissions';

export function App(): JSX.Element {
  const { ready } = useAuth();

  if (!ready) {
    return (
      <div className="login-screen" role="status" aria-live="polite" aria-busy="true">
        <p className="loading-inline" aria-busy="true">
          Loading SenseAI HQ…
        </p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<SignInPage />} />
      <Route
        path="/clients"
        element={
          <Protected>
            <ClientsGate>
              <ClientsPage />
            </ClientsGate>
          </Protected>
        }
      />
      <Route
        path="/device-catalog"
        element={
          <Protected>
            <DeviceCatalogPage />
          </Protected>
        }
      />
      <Route
        path="/manufactured-devices"
        element={
          <Protected>
            <ManufacturedDevicesPage />
          </Protected>
        }
      />
      <Route
        path="/account"
        element={
          <Protected>
            <AccountPage />
          </Protected>
        }
      />
      <Route
        path="/staff"
        element={
          <Protected>
            <StaffPage />
          </Protected>
        }
      />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function Protected({ children }: { children: JSX.Element }): JSX.Element {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function ClientsGate({ children }: { children: JSX.Element }): JSX.Element {
  const meQuery = useStaffMe();
  if (meQuery.isLoading) {
    return (
      <div className="login-screen" role="status" aria-live="polite" aria-busy="true">
        <p className="loading-inline" aria-busy="true">
          Loading…
        </p>
      </div>
    );
  }
  if (meQuery.isError) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdmin(meQuery.data) && !can(meQuery.data, 'clients', 'view')) {
    return <Navigate to="/device-catalog" replace />;
  }
  return children;
}

function HomeRedirect(): JSX.Element {
  const { isLoggedIn } = useAuth();
  const meQuery = useStaffMe();
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  if (meQuery.isLoading) {
    return (
      <div className="login-screen" role="status" aria-live="polite" aria-busy="true">
        <p className="loading-inline" aria-busy="true">
          Loading…
        </p>
      </div>
    );
  }
  if (meQuery.isError) {
    return <Navigate to="/login" replace />;
  }
  if (isAdmin(meQuery.data) || can(meQuery.data, 'clients', 'view')) {
    return <Navigate to="/clients" replace />;
  }
  return <Navigate to="/device-catalog" replace />;
}
